import { DOCUMENT } from '@angular/common';
import { Injectable, NgZone, OnDestroy, Renderer2, RendererFactory2, inject } from '@angular/core';

import { ClientConfigProductName, ProductService, WINDOW } from '@frontend/vanilla/core';
import { Subscription, filter, fromEvent } from 'rxjs';

import { AllowedStickySelectors, SLOT_HEADER_SELECTOR, STICKY_CLASS_NAME } from './models/common.model';

@Injectable({ providedIn: 'root' })
export class HeaderVisibilityOnScrollService implements OnDestroy {
    private readonly document = inject(DOCUMENT);
    private readonly window = inject(WINDOW);
    private readonly renderer: Renderer2 = inject(RendererFactory2).createRenderer(null, null);
    private readonly ngZone = inject(NgZone);
    private readonly productService = inject(ProductService);

    private scrollSubscription?: Subscription;
    private previousScrollTop = 0;

    private headerElement: HTMLElement | null = null;
    private stickyElement: HTMLElement | null = null;

    private isHeaderHidden = false;
    private readonly allowedSelectors = new Set<string>(Object.values(AllowedStickySelectors));

    private setHeaderElement(): void {
        const slotHeader = this.document.querySelector(SLOT_HEADER_SELECTOR);
        if (slotHeader instanceof HTMLElement) {
            this.headerElement = slotHeader;
            this.renderer.addClass(slotHeader, STICKY_CLASS_NAME);
        }
    }

    setStickyElement(navElement: HTMLElement): void {
        if (!(navElement instanceof HTMLElement)) return;
        const tagName = navElement.tagName;
        if (!this.isAllowedSelector(tagName)) return;
        this.stickyElement = navElement;
        this.renderer.addClass(navElement, STICKY_CLASS_NAME);
    }

    private isAllowedSelector(tagName: string): boolean {
        return this.allowedSelectors.has(tagName as AllowedStickySelectors);
    }

    initializeStickyBehavior(): void {
        this.setHeaderElement();
        if (!this.scrollSubscription) {
            this.ngZone.runOutsideAngular(() => {
                this.scrollSubscription = fromEvent(this.window, 'scroll')
                    .pipe(filter(() => this.productService.current.name === ClientConfigProductName.CASINO))
                    .subscribe(() => {
                        requestAnimationFrame(() => this.handleScroll());
                    });
            });
        }
    }

    private handleScroll(): void {
        if (!this.headerElement || !this.stickyElement) return;

        const isScrollingDown = this.isScrollingDownNow();
        const headerHeight = this.headerElement.getBoundingClientRect().height;

        if (this.isHeaderHidden !== isScrollingDown) {
            this.isHeaderHidden = isScrollingDown;
            this.toggleHeaderVisibility(isScrollingDown, headerHeight);
        }
    }

    private toggleHeaderVisibility(hide: boolean, headerHeight: number): void {
        this.renderer.setStyle(this.headerElement, 'top', hide ? `-${headerHeight}px` : `0`);
        this.renderer.setStyle(this.stickyElement, 'top', hide ? `0` : `${headerHeight}px`);
    }

    private isScrollingDownNow(): boolean {
        const current = this.getScrollTop();
        const scrollingDown = current > (this.previousScrollTop ?? 0);
        this.previousScrollTop = current;
        return scrollingDown;
    }

    private getScrollTop(): number {
        return this.window.scrollY || 0;
    }

    ngOnDestroy() {
        this.scrollSubscription?.unsubscribe();
    }
}
